import api from './api.service';
import type { ApiResponse } from '@/types';

function unwrap<T>(res: unknown): T | undefined {
  if (!res || typeof res !== 'object') return undefined;
  const r = res as ApiResponse<T>;
  if (r.data !== undefined) return r.data as T;
  return res as T;
}

export type SalesTargetPeriodType = 'Month' | 'Quarter';

export interface SalesPerformanceMetrics {
  activities: number;
  customer_contacts: number;
  customer_meetings: number;
  demos: number;
  new_leads: number;
  leads_converted: number;
  new_opportunities: number;
  stage_movements: number;
  pipeline_value: number;
  quotations_count: number;
  quotations_value: number;
  won_count: number;
  won_value: number;
  lost_count: number;
  lost_value: number;
  overdue_follow_ups: number;
  commitments_delivered: number;
}

export const SALES_TARGET_METRICS = [
  { field: 'target_activities', actual: 'activities', label: 'Activities', money: false },
  { field: 'target_customer_meetings', actual: 'customer_meetings', label: 'Meetings / visits', money: false },
  { field: 'target_new_leads', actual: 'new_leads', label: 'New leads', money: false },
  { field: 'target_new_opportunities', actual: 'new_opportunities', label: 'New opportunities', money: false },
  { field: 'target_pipeline_value', actual: 'pipeline_value', label: 'Pipeline added', money: true },
  { field: 'target_quotations_value', actual: 'quotations_value', label: 'Quotations value', money: true },
  { field: 'target_won_value', actual: 'won_value', label: 'Won value', money: true },
] as const;

export type SalesTargetField = (typeof SALES_TARGET_METRICS)[number]['field'];

export type SalesTargetValues = Record<SalesTargetField, number | null>;

export interface SalesTarget extends SalesTargetValues {
  id: string;
  period_type: SalesTargetPeriodType;
  period_start: string;
  period_end: string;
  user_id: string | null;
  sales_team_id: string | null;
  user_name: string | null;
  sales_team_name: string | null;
  notes: string | null;
  actuals: SalesPerformanceMetrics;
}

export type SalesTargetInput = Partial<SalesTargetValues> & {
  period_type?: SalesTargetPeriodType;
  period_start?: string;
  user_id?: string | null;
  sales_team_id?: string | null;
  notes?: string | null;
};

export interface SalesTargetListFilters {
  period_type?: SalesTargetPeriodType;
  period_start?: string;
  year?: number;
  user_id?: string;
  sales_team_id?: string;
}

export interface WeeklyPerformanceRow {
  id: string;
  name: string;
  actual: SalesPerformanceMetrics;
  previous: SalesPerformanceMetrics;
  target: SalesTargetValues | null;
}

export interface WeeklySalesPerformance {
  week_start: string;
  week_end: string;
  previous_start: string;
  previous_end: string;
  sales_team_id: string | null;
  sales_team_name: string | null;
  people: WeeklyPerformanceRow[];
  teams: WeeklyPerformanceRow[];
  total: { actual: SalesPerformanceMetrics; previous: SalesPerformanceMetrics };
  activities_by_type: Array<{ type_name: string; this_week: number; last_week: number }>;
  lost_reasons: Array<{
    opportunity_id: string;
    opportunity_number: string;
    opportunity_name: string;
    customer_name: string | null;
    owner_name: string | null;
    value: number;
    reason: string | null;
  }>;
  lost_reason_summary: Array<{ reason: string; count: number; value: number }>;
  stale_deal_days: number;
  stale_deals: Array<{
    id: string;
    opportunity_number: string;
    opportunity_name: string;
    customer_name: string | null;
    owner_name: string | null;
    value: number;
    last_activity_at: string | null;
    days_idle: number;
  }>;
}

/** Weekly report columns, in display order; targeted ones name their target field. */
export type WeeklyMetricGroup = 'activity' | 'pipeline';

export const WEEKLY_PERFORMANCE_METRICS: Array<{
  key: keyof SalesPerformanceMetrics;
  label: string;
  group: WeeklyMetricGroup;
  money?: boolean;
  target?: SalesTargetField;
}> = [
  { key: 'activities', label: 'Activities', group: 'activity', target: 'target_activities' },
  { key: 'customer_contacts', label: 'Customer contacts', group: 'activity' },
  { key: 'customer_meetings', label: 'Meetings / visits', group: 'activity', target: 'target_customer_meetings' },
  { key: 'demos', label: 'Demos', group: 'activity' },
  { key: 'overdue_follow_ups', label: 'Overdue follow-ups', group: 'activity' },
  { key: 'commitments_delivered', label: 'Commitments delivered', group: 'activity' },
  { key: 'new_leads', label: 'New leads', group: 'pipeline', target: 'target_new_leads' },
  { key: 'leads_converted', label: 'Leads converted', group: 'pipeline' },
  { key: 'new_opportunities', label: 'New opportunities', group: 'pipeline', target: 'target_new_opportunities' },
  { key: 'stage_movements', label: 'Stage moves', group: 'pipeline' },
  { key: 'pipeline_value', label: 'Pipeline added', group: 'pipeline', money: true, target: 'target_pipeline_value' },
  { key: 'quotations_value', label: 'Quotations', group: 'pipeline', money: true, target: 'target_quotations_value' },
  { key: 'won_value', label: 'Won', group: 'pipeline', money: true, target: 'target_won_value' },
  { key: 'lost_value', label: 'Lost', group: 'pipeline', money: true },
];

function toParams(filters: object): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(filters)) {
    if (v === undefined || v === null || v === '') continue;
    out[k] = String(v);
  }
  return out;
}

class SalesTargetService {
  async list(filters: SalesTargetListFilters): Promise<SalesTarget[]> {
    const res = await api.get('/sales/sales-targets', { params: toParams(filters) });
    const data = unwrap<SalesTarget[]>(res);
    return Array.isArray(data) ? data : [];
  }

  async save(body: SalesTargetInput, id?: string): Promise<SalesTarget> {
    const res = id ? await api.put(`/sales/sales-targets/${id}`, body) : await api.post('/sales/sales-targets', body);
    return unwrap<SalesTarget>(res) as SalesTarget;
  }

  async remove(id: string): Promise<void> {
    await api.delete(`/sales/sales-targets/${id}`);
  }

  async weeklyPerformance(params: { week_start: string; sales_team_id?: string }): Promise<WeeklySalesPerformance> {
    const res = await api.get('/sales/reports/weekly-sales-performance', { params: toParams(params) });
    return unwrap<WeeklySalesPerformance>(res) as WeeklySalesPerformance;
  }
}

export const salesTargetService = new SalesTargetService();
export default salesTargetService;
